import { SignatureEntity } from '@/entities/signature-entity';
import useSWR from 'swr';
import { route } from 'nextjs-routes';
import { useMemo } from 'react';
import FrontendHttpClient from '@/utils/http-handler/frontend-http-client';
import { useStore } from './use-store';

/**
 * @returns {{signatures: SignatureEntity[], mutate: function():void}}
 */
export const useSignatures = () => {
  const [{ insuredId }] = useStore();

  // Server scopes /api/v2/signatures by the active division (derived from the
  // selected insured). Include insuredId in the SWR key so cache invalidates
  // when the user switches divisions via the insured selector.
  const { data, mutate, isValidating } = useSWR(
    [route({ pathname: '/api/v2/signatures' }), insuredId],
    async ([url]) => {
      const res = await FrontendHttpClient.get(url);
      return res.data;
    }
  );

  const signatures = useMemo(() => {
    return (Array.isArray(data) && data.map(o => new SignatureEntity(o))) || [];
  }, [data]);

  const getDivisionDefault = async () => {
    const result = await FrontendHttpClient.get('/api/v2/divisions/current');
    const currentDivision = result?.data;

    // Force a fresh fetch — callers (e.g. GlobalDefaultSelector) typically
    // call this from a useEffect right after the active division changes,
    // before the SWR-cached signatures list has finished revalidating.
    const sigs = (await mutate()) || [];

    return sigs.find(sig => sig.id === currentDivision.defaultSignatureId);
  };

  return {
    signatures,
    mutate,
    isValidating,
    getDivisionDefault,
  };
};
